import api from "./api"

export const getSubscriptions = async (organizationId) => {
  const response = await api.get("/subscriptions", { params: { organizationId } })
  return response.data
}

/**
 * Fetch paginated subscriptions
 * @param {Object} params - { organizationId, page, limit, search }
 * @returns {Promise<Object>} { success, count, data, limit, page }
 */
export const getSubscriptionsPaginated = async (params = {}) => {
  // Ensure params is always a valid object
  const validParams = params && typeof params === "object" ? params : {};

  // Get organizationId from user context if not provided
  if (!validParams.organizationId) {
    const user = JSON.parse(localStorage.getItem("user") || "{}")
    if (user && user.organizationId) {
      validParams.organizationId = user.organizationId
    }
  }

  const response = await api.get("/subscriptions/paginated", { params: validParams });
  return response.data;
};

export const getSubscription = async (customerId) => {
  const response = await api.get("/subscriptions/customer", { params: { customerId } })
  return response.data
}

export const getSubscriptionHistory = async (customerId) => {
  const response = await api.get("/subscriptions/customer/history", { params: { customerId } })
  return response.data
}

export const createSubscription = async (subscriptionData, masterPassword) => {
  const headers = {}
  if (masterPassword) {
    headers['x-master-password'] = masterPassword
  }
  const response = await api.post("/subscriptions", subscriptionData, { headers })
  return response.data
}

export const cancelSubscription = async (id, reason, cancelType = "end_of_cycle") => {
  const response = await api.post(`/subscriptions/${id}/cancel`, { reason, cancelType })
  return response.data
}

export const pauseSubscription = async (id) => {
  const response = await api.put(`/subscriptions/${id}/pause`)
  return response.data
}

export const resumeSubscription = async (id) => {
  const response = await api.post(`/subscriptions/${id}/resume`)
  return response.data
}

export const changePlan = async (id, newPlanId, billingCycle, masterPassword) => {
  const headers = {}
  if (masterPassword) {
    headers['x-master-password'] = masterPassword
  }
  const response = await api.post(`/subscriptions/${id}/change-plan`, { 
    newPlanId,
    billingCycle 
  }, { headers })
  
  // If there's a payment session URL, redirect to it
  if (response.data.data.paymentSession?.url) {
    window.location.href = response.data.data.paymentSession.url
    return null
  }
  
  // If there's a payment intent, return it for client-side handling
  if (response.data.data.paymentIntent) {
    return {
      ...response.data.data,
      requiresPayment: true
    }
  }
  
  return response.data
}

// Update these to use the new /api/plans endpoint
export const getPlans = async (params = {}) => {
  const response = await api.get("/plans", { params })
  return response.data
}

export const getPlan = async (id) => {
  const response = await api.get(`/plans/${id}`)
  return response.data
}

export const createPlan = async (planData, params = {}) => {
  const response = await api.post("/plans", planData, { params })
  return response.data
}

export const updatePlan = async (id, planData, masterPassword) => {
  const config = masterPassword ? { headers: { "x-master-password": masterPassword } } : {}
  const response = await api.put(`/plans/${id}`, planData, config)
  return response.data
}

export const deletePlan = async (id) => {
  const response = await api.delete(`/plans/${id}`)
  return response.data
}

// Add this function if it doesn't already exist
export const getCustomerSubscriptions = async (customerId) => {
  const response = await api.get(`/subscriptions/customer/${customerId}`)
  return response.data
}

// Add this function to get a subscription by its associated payment intent
export const getSubscriptionByPaymentIntent = async (paymentIntentId) => {
  const response = await api.get(`/subscriptions/payment-intent/${paymentIntentId}`)
  return response.data
}

